/**
 * The When module's copy (1.O-1 deliverables 3 to 7), pure: every label, preview sentence and
 * refusal in US English, so the report's Decisions list one file. Month, era and season names come
 * from the compiled calendar through 1.K-1's formatters; titles are written as the GM wrote them.
 */
import {
  type CompiledCalendar,
  formatTick,
  type ParsedWhen,
  type TickReviewReason,
} from '@codex/calendar';
import type { ParseErrorCode, PartOfUnit, Precision, Qualifier } from '@codex/contracts';

export const cap = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** "Month", "Day", "Season". */
export function precisionWord(precision: Precision): string {
  return cap(precision);
}

/** "Early", "Mid", "Late" (the parts of 1.K-1's `PART_WORDS`). */
export function partWord(part: PartOfUnit | undefined): string | null {
  return part === undefined ? null : cap(part);
}

/** "About" or "Uncertain"; nothing for an exact date. */
export function qualifierWord(qualifier: Qualifier): string | null {
  if (qualifier === 'approximate') return 'About';
  return qualifier === 'uncertain' ? 'Uncertain' : null;
}

const ROLE_WORDS: Readonly<Record<string, string>> = {
  occurs: 'Happens',
  born: 'Born',
  died: 'Died',
  session: 'Session',
};

/** A placement role in words: "Happens", "Born", "Died", "Session", else the role itself. */
export function roleWord(role: string): string {
  return ROLE_WORDS[role] ?? role;
}

const STATUS_WORDS: Readonly<Record<string, string>> = {
  canon: 'Canon',
  planned: 'Planned',
  rumor: 'Rumor',
  alternate: 'Alternate',
  retired: 'Retired',
};

export function statusWord(status: string): string {
  return STATUS_WORDS[status] ?? status;
}

/** The word of the phrase where reading failed, for a reason that names it. */
export function wordAt(text: string, at: number): string {
  const match = /^[^\s,–-]+/.exec(text.slice(Math.max(0, at)));
  return match ? match[0] : text.trim();
}

const PARSE_REASONS: Readonly<Record<ParseErrorCode, string>> = {
  empty: 'Type a date, a phrase such as "3 days after @Page", or "undated".',
  unknown_name: 'No month is named {word} in this calendar.',
  ambiguous_name: '{word} could be more than one name in this calendar.',
  missing_year: 'Add a year.',
  now_unset: "NOW isn't set yet.",
  day_out_of_range: "That day isn't in that month.",
  time_out_of_range: "That time isn't on the calendar's clock.",
  week_out_of_range: "That week isn't in that year.",
  mixed_precision: 'Both sides of a range need the same precision.',
  anchor_expected: 'Name a page after @.',
  bad_lag: 'The gap reads as a count and a unit, such as "3 days" or "3–5 days".',
  unexpected_text: '"{word}" can\'t be read here.',
  calendar_invalid: "The calendar can't be read.",
};

const TICK_REASONS: Readonly<Record<TickReviewReason, string>> = {
  calendar_invalid: "The calendar can't be read.",
  unknown_key: "A name in this date isn't in the calendar.",
  incomplete_parts: 'This date is missing a part.',
  day_out_of_range: "That day isn't in that month.",
  time_out_of_range: "That time isn't on the calendar's clock.",
  week_out_of_range: "That week isn't in that year.",
  not_in_year: "That date isn't in that year.",
  outside_era: 'That date is outside its era.',
  inverted_range: 'The range ends before it starts.',
  out_of_tick_range: "That date is too far from the calendar's start.",
  key_removed: 'A name in this date was removed from the calendar.',
};

/** A parse error or a review reason in words; `{word}` takes the word where reading failed. */
export function reasonText(reason: string, word = ''): string {
  const parse = PARSE_REASONS[reason as ParseErrorCode];
  const text = parse ?? TICK_REASONS[reason as TickReviewReason] ?? "This date can't be read.";
  return text.replaceAll('{word}', word);
}

export const KIND_NOT_ALLOWED = "That kind of date can't be used here.";
export const UNRESOLVED_ANCHOR = 'Pick the page from the list under the field.';
export const UNDATED_TEXT = 'Undated: it waits in the Unplaced tray until something orders it';
export const NO_CALENDAR = 'No calendar yet.';
export const CALENDAR_FAILED = "The calendar couldn't be loaded.";
export const PLACED_ONCE_SAVED = 'Placed in order once saved';

type Relative = Extract<ParsedWhen, { kind: 'relative' }>;

/** A gap in words: "3 days", "3–5 days", "at least 3 days", "within 3 days". */
export function lagText(lag: NonNullable<Relative['lag']>): string {
  const unit = (n: number) => (n === 1 ? lag.unit : `${lag.unit}s`);
  if (lag.min !== null && lag.max !== null) {
    return lag.min === lag.max
      ? `${lag.min} ${unit(lag.min)}`
      : `${lag.min}–${lag.max} ${unit(lag.max)}`;
  }
  if (lag.min !== null) return `at least ${lag.min} ${unit(lag.min)}`;
  return `within ${lag.max} ${unit(lag.max ?? 0)}`;
}

/** A relative read's rule in words: "3 days after the end of The Siege of Vesk". */
export function ruleText(read: Relative, title: string): string {
  const lag = read.lag === null ? '' : `${lagText(read.lag)} `;
  switch (read.relation) {
    case 'after':
      return cap(`${lag}after the end of ${title}`);
    case 'before':
      return cap(`${lag}before the start of ${title}`);
    case 'during':
      return `Within ${title}`;
    default:
      return `At the same time as ${title}`;
  }
}

export function sessionRuleText(number: number): string {
  return `At the same time as session ${number}`;
}

/**
 * "Between A and B" (both inclusive; `hi` is exclusive), or for one open side "After A" and
 * "Before B" (a rule's bounds) or "From A on" and "Until B" (a point's).
 */
export function boundsText(
  calendar: CompiledCalendar,
  lo: number | null,
  hi: number | null,
  precision: Precision,
  open: 'rule' | 'point' = 'rule',
): string | null {
  const a = lo === null ? null : formatTick(calendar, lo, precision);
  const b = hi === null ? null : formatTick(calendar, hi - 1, precision);
  if (a !== null && b !== null) return a === b ? `On ${a}` : `Between ${a} and ${b}`;
  if (a !== null) return open === 'rule' ? `After ${a}` : `From ${a} on`;
  if (b === null) return null;
  return open === 'rule' ? `Before ${b}` : `Until ${b}`;
}

const FINER: Readonly<Record<Precision, Precision>> = {
  era: 'year',
  century: 'year',
  decade: 'year',
  year: 'month',
  season: 'day',
  month: 'day',
  week: 'day',
  day: 'hour',
  hour: 'minute',
  minute: 'minute',
};

/** The precision a point's bounds are shown at: one step finer when a part narrows it. */
export function boundsPrecision(precision: Precision, part: PartOfUnit | undefined): Precision {
  // A season says nothing at its own precision ("On Spring 1042"): its days are shown.
  if (precision === 'season') return 'day';
  return part === undefined ? precision : FINER[precision];
}

export function anchorUseText(name: string, date: string | null): string {
  return `On ${name}, ${date ?? 'Undated'}`;
}

export function createAutoText(title: string): string {
  return `${title} has no date yet: an undated event will be added to it`;
}

export function pickerLabel(title: string): string {
  return `Which date of ${title}?`;
}

export function datedNotice(title: string, canonical: string): string {
  return `Dated ${title}: ${canonical}.`;
}

export const REFUSALS = {
  conflict: "This date changed elsewhere. Your change wasn't saved.",
  trashed: 'This page, or the page it names, is in the trash.',
  canonRole: 'This page already has a canon {role} date. Change that one instead.',
  pick: 'Pick which date of the page to use.',
  blockUnsaved: "This paragraph isn't saved yet. Try again in a moment.",
  lag: 'A gap can\'t go with "during" or "same time as".',
  invalid: "The date couldn't be saved: the chronology refused it.",
  notFound: 'This page or date no longer exists.',
  network: "The server couldn't be reached. Your change wasn't saved.",
  other: "The date couldn't be saved.",
  noSession: 'That session has no date of its own yet.',
  sessionsUnavailable: "The sessions couldn't be read.",
} as const;

export const HIDDEN_SELECTION = "Text inside a secret or GM note can't become an event.";
export const NO_SELECTION = 'Select a sentence in the page first.';

// ------------------------------------------------------------------ the Chronology tab (s2)

/** A constraint's status in words; nothing for an active one. */
export const CONSTRAINT_STATUS_WORDS: Readonly<Record<string, string>> = {
  dropped_cycle: 'Ignored: it closes a loop',
  contradiction: 'Contradicts a fixed date',
  lag_unresolved: "The gap can't be measured yet",
};

/** The other side of a rule when its page could not be found (retired, or beyond the Chronicle). */
export const ANOTHER_DATE = 'another date';
export const UNPLACED = 'Unplaced';
/** A link boundary on a placement edge whose window is not known. */
export const UNKNOWN_DATE = 'an unknown date';
export const INVALID_POINT = "This date can't be placed on the calendar.";
export const INVALID_SPAN = "Its dates can't be worked out";
export const CHRONO_OFF = "Chronology isn't turned on.";
export const TAB_FAILED = "The dates couldn't be loaded.";
export const TAB_EMPTY = 'No dates yet. Add one, or use the When pill.';
export const REMOVE_TITLE = 'Remove this date?';
export const REMOVE_BODY = 'Its order rules go with it.';
export const RULE_REMOVED = 'Rule removed.';
export const DATE_REMOVED = 'Date removed.';
export const NOW_UNSET = "NOW isn't set yet.";

export function ageAtNowText(age: string, now: string): string {
  return `${age} at NOW (${now})`;
}

export function ageAtDeathText(age: string, died: string): string {
  return `${age} at death (${died})`;
}

// ------------------------------------------------------------------------- the NOW chip (s2)

export const SET_NOW = 'Set NOW';
export const SET_NOW_FIRST = 'Set NOW first.';
export const NOW_NEEDS_A_DATE = 'NOW needs a date, such as "14 Ember 1042, 20:00", or "now".';
export const ADVANCE_NEEDS_A_GAP = 'Type how long, such as "8 hours" or "1 day".';
export const NOW_CONFLICT =
  "NOW changed elsewhere and is shown as it is now. Your change wasn't saved.";
export const NOW_FAILED = "NOW couldn't be changed.";
export const NOW_UNAVAILABLE = 'NOW unavailable';
export const NOW_UNREAD = "NOW couldn't be read. Try again before changing it.";
export const NOTHING_TO_UNDO = 'No advance to undo.';

export function nowChipLabel(text: string): string {
  return `In-world date: ${text}`;
}

export function nowIsText(text: string): string {
  return `NOW is ${text}.`;
}
